import React, { useState, useEffect, useContext, useRef } from 'react';
import { Button, Input } from 'reactstrap';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ReactSelect from 'react-select';
import { useForm, Controller } from 'react-hook-form';
import _ from 'lodash';
import 'boxicons';

import HeaderContext from './TasksFilter.Context';
import ContainerLabelDate from 'components/Container-Label-Date';
import { TasksFilterLayout } from './styles';

const TasksFilter = ({ toggleFilter, accountId, show }) => {
  const formConfig = {
    defaultValues: {
      assignedTo: [],
      priority: [],
      focuses: [],
      search: '',
      startStartDate: new Date(),
      endStartDate: null,
      startImplementationDate: new Date(),
      endImplementationDate: null,
      startDeliveryDate: new Date(),
      endDeliveryDate: null,
    },
  };
  const { t: translate } = useTranslation();
  const { control, reset, getValues, setValue, watch } = useForm(formConfig);
  const { setFilter, focuses, members } = useContext(HeaderContext);

  const [isStartDatePickerOpen, setIsStartDatePickerOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [isImplementationDatePickerOpen, setIsImplementationDatePickerOpen] = useState(false);
  const [implementationDate, setImplementationDate] = useState('');
  const [isDeliveryDatePickerOpen, setIsDeliveryDatePickerOpen] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState('');

  const inputRef = useRef(null);

  const handleToggle = () => toggleFilter((prevState) => !prevState);

  const cleanFilters = () => {
    setStartDate('');
    setImplementationDate('');
    setDeliveryDate('');
    reset();
    applyFilters();
  };

  const applyFilters = () => {
    const dataObj = getValues();
    setFilter(dataObj);
  };

  useEffect(() => {
    if (!isStartDatePickerOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 10);
    }
  }, [isStartDatePickerOpen]);

  return (
    <div className="header-wrapper" style={{ display: show ? 'flex' : 'none' }}>
      <TasksFilterLayout>
        <div className="filter-menu m-0">
          <form className="filter-menu">
            <div>
              <div
                className="position-relative"
                onMouseLeave={() => setIsStartDatePickerOpen(false)}
              >
                <Controller
                  control={control}
                  name="startDate"
                  render={({ field }) => (
                    <Input
                      {...field}
                      innerRef={(e) => {
                        field.ref(e);
                        inputRef.current = e;
                      }}
                      value={startDate || ''}
                      placeholder={startDate ? startDate : translate('Start date')}
                      id="input-date"
                      style={{ minWidth: '190px' }}
                      onClick={() => setIsStartDatePickerOpen(!isStartDatePickerOpen)}
                      readOnly
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          applyFilters();
                        }
                      }}
                    />
                  )}
                />
                {isStartDatePickerOpen && (
                  <ContainerLabelDate
                    date={startDate}
                    setDate={setStartDate}
                    watch={watch}
                    setValue={setValue}
                    getValues={getValues}
                    setIsDatePickerOpen={setIsStartDatePickerOpen}
                    inputRef={inputRef}
                    left
                    top="36px"
                    startDateName="startStartDate"
                    endDateName="endStartDate"
                    onApplyFilter={applyFilters}
                  />
                )}
              </div>

              <div
                className="position-relative"
                onMouseLeave={() => setIsDeliveryDatePickerOpen(false)}
              >
                <Controller
                  control={control}
                  name="deliveryDate"
                  render={({ field }) => (
                    <Input
                      {...field}
                      innerRef={(e) => {
                        field.ref(e);
                        inputRef.current = e;
                      }}
                      value={deliveryDate || ''}
                      placeholder={deliveryDate ? deliveryDate : translate('Delivery date')}
                      id="input-date"
                      style={{ minWidth: '190px' }}
                      onClick={() => setIsDeliveryDatePickerOpen(!isDeliveryDatePickerOpen)}
                      readOnly
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          applyFilters();
                        }
                      }}
                    />
                  )}
                />
                {isDeliveryDatePickerOpen && (
                  <ContainerLabelDate
                    date={deliveryDate}
                    setDate={setDeliveryDate}
                    watch={watch}
                    setValue={setValue}
                    getValues={getValues}
                    setIsDatePickerOpen={setIsDeliveryDatePickerOpen}
                    inputRef={inputRef}
                    left
                    top="36px"
                    startDateName="startDeliveryDate"
                    endDateName="endDeliveryDate"
                    onApplyFilter={applyFilters}
                  />
                )}
              </div>

              <div
                className="position-relative"
                onMouseLeave={() => setIsImplementationDatePickerOpen(false)}
              >
                <Controller
                  control={control}
                  name="implementationDate"
                  render={({ field }) => (
                    <Input
                      {...field}
                      innerRef={(e) => {
                        field.ref(e);
                        inputRef.current = e;
                      }}
                      value={implementationDate || ''}
                      placeholder={
                        implementationDate ? implementationDate : translate('Implementation date')
                      }
                      id="input-date"
                      style={{ minWidth: '190px' }}
                      onClick={() =>
                        setIsImplementationDatePickerOpen(!isImplementationDatePickerOpen)
                      }
                      readOnly
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          applyFilters();
                        }
                      }}
                    />
                  )}
                />
                {isImplementationDatePickerOpen && (
                  <ContainerLabelDate
                    date={implementationDate}
                    setDate={setImplementationDate}
                    watch={watch}
                    setValue={setValue}
                    getValues={getValues}
                    setIsDatePickerOpen={setIsImplementationDatePickerOpen}
                    inputRef={inputRef}
                    left
                    top="36px"
                    startDateName="startImplementationDate"
                    endDateName="endImplementationDate"
                    onApplyFilter={applyFilters}
                  />
                )}
              </div>

              <Controller
                control={control}
                name="priority"
                render={({ field }) => (
                  <ReactSelect
                    {...field}
                    value={field.value}
                    placeholder={translate('Priority')}
                    className="basic-multi-select filter-select"
                    style={{ minWidth: '250px', width: '250px' }}
                    options={[
                      { value: 'low', label: translate('Low') },
                      { value: 'normal', label: translate('Normal') },
                      { value: 'high', label: translate('High') },
                      { value: 'urgent', label: translate('Urgent') },
                    ]}
                    isMulti
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        applyFilters();
                      }
                    }}
                  />
                )}
              />

              <Controller
                control={control}
                name="focuses"
                render={({ field }) => (
                  <ReactSelect
                    {...field}
                    value={field.value}
                    placeholder={translate('Focuses')}
                    className="basic-multi-select filter-select"
                    style={{ minWidth: '250px', width: '250px' }}
                    options={focuses}
                    isMulti
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        applyFilters();
                      }
                    }}
                  />
                )}
              />
            </div>

            <div>
              <Controller
                control={control}
                name="assignedTo"
                render={({ field }) => (
                  <ReactSelect
                    {...field}
                    value={field.value}
                    placeholder={translate('Assigned To')}
                    className="basic-multi-select filter-select"
                    options={members}
                    isMulti
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        applyFilters();
                      }
                    }}
                    styles={{
                      minWidth: '250px',
                      width: '250px',
                    }}
                  />
                )}
              />

              <Controller
                control={control}
                name="search"
                render={({ field }) => (
                  <div className="search" style={{ minWidth: '300px' }}>
                    <box-icon name="search" color="#606060" />
                    <Input
                      {...field}
                      value={field.value}
                      placeholder={translate('Search')}
                      required
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          applyFilters();
                        }
                      }}
                    />
                  </div>
                )}
              />
            </div>
          </form>
        </div>

        <div className="filter-btn">
          <Button className="filter-btn-apply" onClick={applyFilters}>
            <box-icon name="check" color="#FFF" />
          </Button>
          <Button className="filter-btn-clear" onClick={cleanFilters}>
            <box-icon name="eraser" color="#FFF" />
          </Button>
          <Button className="filter-btn-back" onClick={handleToggle}>
            {translate('Go back')}
          </Button>
        </div>
      </TasksFilterLayout>
    </div>
  );
};

TasksFilter.propTypes = {
  toggleFilter: PropTypes.func,
  accountId: PropTypes.string,
  show: PropTypes.bool,
};

export default TasksFilter;
